import React, { useCallback } from 'react';
import { ViewStyle } from 'react-native';
import { Endpoints, IOStatusEnum, SendStatusEnum } from '@matap/shared';
import styled from 'styled-components/native';
import { useAppDispatch } from '@/hooks/use.redux';
import { useActiveVisit } from '@/hooks/use.active.visit';
import { AppTextView } from '@/components/base/app.text.view';
import { Box } from '@/components/base/box';
import { AppIconButton } from '@/components/base/app.icon.button';
import { AppPressable } from '@/components/base/app.pressable';
import { FileAssetInfo } from '../../../file-manager/file.asset';
import { actionUpdateChat } from '@/redux';

interface Props {
  fileState: FileAssetInfo;
  chatId: string;
  style?: ViewStyle;
}

function BaseDownloadUploadStateView({ fileState: { fileName, source, state, progress }, chatId, style }: Props) {
  const dispatch = useAppDispatch();
  const { activeVisitId } = useActiveVisit();

  const cancel = useCallback(() => {
    if (state === IOStatusEnum.UPLOADING || state === IOStatusEnum.WAITING_FOR_UPLOAD) {
      //source.cancelUpload();
      //dispatch(actionDeleteChat(chatId));
    } else {
      // source.cancelDownload();
    }
  }, [dispatch, state]);

  const upload = useCallback(
    () =>
      source.upload(Endpoints.chats.user.uploadChatFile.path, { chatid: chatId, visitid: activeVisitId }).then((res) => {
        if (res) {
          dispatch(actionUpdateChat({ _id: chatId, sendStatus: SendStatusEnum.SENT }));
        }
      }),
    [source, chatId, activeVisitId, dispatch],
  );

  const download = useCallback(() => source.forceDownload(), [source]);

  // Unified press handler for the main container
  const handlePress = useCallback(() => {
    if (state === IOStatusEnum.DOWNLOADING || state === IOStatusEnum.UPLOADING) {
      cancel();
    } else if (state === IOStatusEnum.WAITING_FOR_DOWNLOAD) {
      download();
    } else if (state === IOStatusEnum.WAITING_FOR_UPLOAD) {
      upload();
    }
  }, [state, cancel, download, upload]);

  return (
    <Container style={style} onPress={handlePress}>
      {/* pointerEvents="none" forces all touches to pass through to the Container */}
      <Box pointerEvents="none" flex={1}>
        {(state === IOStatusEnum.DOWNLOADING || state === IOStatusEnum.UPLOADING) && (
          <ActionButtonContainer pointerEvents={'auto'}>
            <ProgressingBox>
              <AppTextView text={`${progress} %`} textColor={'white'} />
            </ProgressingBox>
          </ActionButtonContainer>
        )}
        {state === IOStatusEnum.WAITING_FOR_DOWNLOAD && (
          <ActionButtonContainer pointerEvents={'auto'}>
            <AppIconButton icon={'download'} size={'medium'} />
          </ActionButtonContainer>
        )}
        {state === IOStatusEnum.WAITING_FOR_UPLOAD && (
          <ActionButtonContainer pointerEvents={'auto'}>
            <AppIconButton icon={'upload'} size={'medium'} />
          </ActionButtonContainer>
        )}
      </Box>
    </Container>
  );
}

// Changed from Box to AppPressable so it registers touches
const Container = styled(AppPressable)`
  width: 68px;
  height: 68px;
`;

const ActionButtonContainer = styled(Box)`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.colors.jadeLight};
  border-color: white;
  border-width: 1px;
  shadow-color: #000;
  shadow-opacity: 0.06;
  shadow-radius: 8px;
  shadow-offset: 0px 4px;
  elevation: 3;
`;

// Changed from AppPressable to Box because the Container now handles the press
const ProgressingBox = styled(Box)`
  width: 90%;
  height: 90%;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.jade};
`;

export const DownloadUploadStateView = React.memo(BaseDownloadUploadStateView);
